import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Gavel } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Sprint K · Legal Analysis Run Detail
 * Route: /app/legal/analysis/:orchId/runs/:runId
 *
 * Renders each workforce_action node output with the appended footer
 * (backend-baked). For platform_owner, the footer is absent per K-8 —
 * the UI renders raw text as-is.
 */
export default function LegalAnalysisRunDetail() {
    const { orchId, runId } = useParams();
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";
    const [run, setRun] = useState(null);
    const [orch, setOrch] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get(`/orchestration-runs/${runId}`);
                if (!cancelled) setRun(r.data.run || r.data);
            } catch (err) {
                console.warn("run poll failed:", err?.message);
            }
        };
        load();
        api
            .get(`/orchestrations/${orchId}`)
            .then((r) => setOrch(r.data))
            .catch(() => {});
        // Poll every 3s while running / awaiting_approval
        const iv = setInterval(() => {
            const s = run?.status;
            if (s === "success" || s === "failed" || s === "completed") return;
            load();
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(iv);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orchId, runId, run?.status]);

    const nodeRuns = run?.node_runs || [];
    const statusColor = {
        running:            { bg: "rgba(59,130,246,0.14)",  border: "rgba(59,130,246,0.35)", color: "#93c5fd", label: "RUNNING" },
        awaiting_approval:  { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.35)", color: "#fbbf24", label: "AWAITING ATTORNEY" },
        success:            { bg: "rgba(52,211,153,0.14)",  border: "rgba(52,211,153,0.35)", color: "#34d399", label: "COMPLETE" },
        completed:          { bg: "rgba(52,211,153,0.14)",  border: "rgba(52,211,153,0.35)", color: "#34d399", label: "COMPLETE" },
        failed:             { bg: "rgba(239,68,68,0.14)",   border: "rgba(239,68,68,0.35)",  color: "#f87171", label: "FAILED" },
    };

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="legal-analysis-run-detail">
            <div className="max-w-4xl mx-auto p-8">
                <Link
                    to="/app/legal/analysis"
                    className="inline-flex items-center gap-1.5 text-sm mb-6"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
                    Back to Legal Analysis
                </Link>

                <div className="flex items-start gap-4 mb-6">
                    <div
                        className="w-12 h-12 rounded-lg grid place-items-center flex-shrink-0"
                        style={{
                            background: "rgba(139,92,246,0.14)",
                            border: "1px solid rgba(139,92,246,0.35)",
                        }}
                    >
                        <Gavel
                            className="w-6 h-6"
                            style={{ color: "var(--atlas-primary)" }}
                            strokeWidth={1.75}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Legal Analysis · Run
                        </div>
                        <h1
                            className="text-2xl font-semibold mt-1 truncate"
                            style={{ color: "var(--atlas-text-primary)" }}
                        >
                            {orch?.name || "Legal analysis"}
                        </h1>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {run?.status && (
                                <span
                                    className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                                    style={{
                                        background: statusColor[run.status]?.bg,
                                        border: `1px solid ${statusColor[run.status]?.border}`,
                                        color: statusColor[run.status]?.color,
                                    }}
                                    data-testid="legal-run-status"
                                >
                                    {statusColor[run.status]?.label || run.status.toUpperCase()}
                                </span>
                            )}
                            {isOwner && (
                                <span
                                    className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                                    style={{
                                        background: "rgba(245,158,11,0.14)",
                                        border: "1px solid rgba(245,158,11,0.35)",
                                        color: "#fbbf24",
                                    }}
                                >
                                    OWNER · DISCLAIMERS BYPASSED
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {!run && (
                    <div className="p-6 text-center text-sm" style={{ color: "var(--atlas-text-tertiary)" }}>
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                        Loading run…
                    </div>
                )}

                {nodeRuns.map((nr) => {
                    const isWorker = nr.node_type === "workforce_action";
                    const isApproval = nr.node_type === "approval_gate";
                    const outputText =
                        typeof nr.output === "string"
                            ? nr.output
                            : nr.output?.text || nr.output?.message || "";
                    return (
                        <div
                            key={nr.node_id}
                            className="mb-4 rounded-xl p-5"
                            style={{
                                background: "var(--atlas-bg-elevated)",
                                border: "1px solid var(--atlas-border-soft)",
                            }}
                            data-testid={`legal-run-node-${nr.node_id}`}
                        >
                            <div className="flex items-center gap-2 mb-3">
                                {nr.status === "success" && (
                                    <CheckCircle2
                                        className="w-4 h-4"
                                        style={{ color: "#34d399" }}
                                        strokeWidth={2}
                                    />
                                )}
                                {nr.status === "awaiting_approval" && (
                                    <ShieldCheck
                                        className="w-4 h-4"
                                        style={{ color: "#fbbf24" }}
                                        strokeWidth={2}
                                    />
                                )}
                                {nr.status === "failed" && (
                                    <AlertTriangle
                                        className="w-4 h-4"
                                        style={{ color: "#f87171" }}
                                        strokeWidth={2}
                                    />
                                )}
                                <span
                                    className="text-sm font-semibold"
                                    style={{ color: "var(--atlas-text-primary)" }}
                                >
                                    {nr.label}
                                </span>
                                <span
                                    className="ml-auto text-[10px]"
                                    style={{ color: "var(--atlas-text-tertiary)" }}
                                >
                                    {new Date(nr.started_at).toLocaleString()}
                                </span>
                            </div>
                            {isWorker && outputText && (
                                <pre
                                    className="whitespace-pre-wrap text-xs leading-relaxed p-3 rounded-lg"
                                    style={{
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-soft)",
                                        color: "var(--atlas-text-primary)",
                                        fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
                                    }}
                                    data-testid="legal-run-output"
                                >
                                    {outputText}
                                </pre>
                            )}
                            {isApproval && (
                                <div
                                    className="text-xs"
                                    style={{ color: "var(--atlas-text-secondary)" }}
                                >
                                    Approval required from role: <strong>{nr.approval?.approver_role || "attorney"}</strong>
                                    {nr.status === "awaiting_approval" && (
                                        <div className="mt-1" style={{ color: "#fbbf24" }}>
                                            Waiting for attorney-of-record acknowledgment…
                                        </div>
                                    )}
                                </div>
                            )}
                            {nr.error && (
                                <div className="mt-2 text-xs" style={{ color: "#f87171" }}>
                                    Error: {nr.error}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Non-owner belt-and-suspenders footer — the backend also bakes the
                    footer into each output. This card renders in the layout so
                    it's visible even if a workforce_action hasn't completed yet. */}
                {!isOwner && (
                    <div
                        className="mt-8 p-4 rounded-lg text-xs leading-relaxed"
                        style={{
                            background: "var(--atlas-bg-deep)",
                            border: "1px solid var(--atlas-border-soft)",
                            color: "var(--atlas-text-tertiary)",
                        }}
                        data-testid="legal-run-footer"
                    >
                        <div className="font-semibold" style={{ color: "var(--atlas-text-secondary)" }}>
                            AI-Generated Legal Information · NOT Legal Advice
                        </div>
                        <div className="mt-1">
                            This analysis was generated by Nexus One AI Workforce Members and
                            reflects Nexus One's understanding of publicly available law as of the
                            analysis date. It is legal <strong>information</strong> only and does{" "}
                            <strong>not</strong> constitute legal advice. Do not rely on this
                            analysis for any actual legal decision without independent review by a
                            licensed attorney admitted to practice in your jurisdiction. Nexus One
                            is not a law firm. No attorney-client relationship is created by use of
                            this platform.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
